import { ChangeDetectionStrategy, Component, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideArrowLeft, lucideUpload } from '@ng-icons/lucide';

import { ZardButtonComponent } from '../../shared/components/button';
import { ZardCardComponent } from '../../shared/components/card';
import { ZardSelectComponent } from '../../shared/components/select/select.component';
import { ZardSelectItemComponent } from '../../shared/components/select/select-item.component';

export interface PropertyFormData {
  name: string;
  type: string;
  address: string;
  city: string;
  postalCode: string;
  surface: string;
  rooms: string;
  price: string;
  description: string;
  yearBuilt: string;
  floor: string;
  energyClass: string;
}

@Component({
  selector: 'app-add-property-form',
  imports: [
    FormsModule,
    NgIcon,
    ZardButtonComponent,
    ZardCardComponent,
    ZardSelectComponent,
    ZardSelectItemComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [provideIcons({ lucideArrowLeft, lucideUpload })],
  template: `
    <div class="min-h-screen bg-gray-50 pb-20">
      <div class="bg-white border-b sticky top-0 z-10">
        <div class="max-w-2xl mx-auto px-4 py-4">
          <div class="flex items-center gap-4">
            <button type="button" (click)="back.emit()" class="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <ng-icon name="lucideArrowLeft" class="w-5 h-5 text-gray-700" />
            </button>
            <h1 class="text-xl text-gray-900">Ajouter un bien</h1>
          </div>
        </div>
      </div>

      <div class="max-w-2xl mx-auto px-4 py-6">
        <form (ngSubmit)="handleSubmit()" class="space-y-6">
          <z-card class="p-6 border-0 shadow-sm">
            <label class="text-gray-900 mb-4 block" for="photos">Photos du bien</label>
            <div class="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-[#00A878] transition-colors cursor-pointer">
              <ng-icon name="lucideUpload" class="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p class="text-sm text-gray-600">Cliquez pour ajouter des photos</p>
              <p class="text-xs text-gray-400 mt-1">PNG, JPG jusqu'à 10MB</p>
            </div>
          </z-card>

          <z-card class="p-6 border-0 shadow-sm space-y-4">
            <h2 class="text-lg text-gray-900">Informations générales</h2>
            <div class="space-y-2">
              <label class="text-gray-700 text-sm" for="name">Nom du bien</label>
              <input z-input [(ngModel)]="formData.name" id="name" name="name" placeholder="Ex: Appartement Centre-ville" class="h-11" required />
            </div>
            <div class="space-y-2">
              <label class="text-gray-700 text-sm" for="type">Type de bien</label>
              <z-select [(ngModel)]="formData.type" id="type" name="type" zPlaceholder="Sélectionnez un type">
                <z-select-item zValue="appartement">Appartement</z-select-item>
                <z-select-item zValue="maison">Maison</z-select-item>
                <z-select-item zValue="studio">Studio</z-select-item>
                <z-select-item zValue="duplex">Duplex</z-select-item>
                <z-select-item zValue="loft">Loft</z-select-item>
              </z-select>
            </div>
          </z-card>

          <z-card class="p-6 border-0 shadow-sm space-y-4">
            <h2 class="text-lg text-gray-900">Adresse</h2>
            <div class="space-y-2">
              <label class="text-gray-700 text-sm" for="address">Adresse complète</label>
              <input z-input [(ngModel)]="formData.address" name="address" id="address" placeholder="Numéro et nom de rue" class="h-11" required />
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div class="space-y-2">
                <label class="text-gray-700 text-sm" for="city">Ville</label>
                <input z-input [(ngModel)]="formData.city" name="city" id="city" placeholder="Ville" class="h-11" required />
              </div>
              <div class="space-y-2">
                <label class="text-gray-700 text-sm" for="postalCode">Code postal</label>
                <input z-input [(ngModel)]="formData.postalCode" name="postalCode" id="postalCode" placeholder="75001" class="h-11" required />
              </div>
            </div>
          </z-card>

          <z-card class="p-6 border-0 shadow-sm space-y-4">
            <h2 class="text-lg text-gray-900">Caractéristiques</h2>
            <div class="grid grid-cols-2 gap-4">
              <div class="space-y-2">
                <label class="text-gray-700 text-sm" for="surface">Surface (m²)</label>
                <input z-input type="number" [(ngModel)]="formData.surface" name="surface" id="surface" placeholder="50" class="h-11" required />
              </div>
              <div class="space-y-2">
                <label class="text-gray-700 text-sm" for="rooms">Nombre de pièces</label>
                <input z-input type="number" [(ngModel)]="formData.rooms" name="rooms" id="rooms" placeholder="3" class="h-11" required />
              </div>
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div class="space-y-2">
                <label class="text-gray-700 text-sm" for="floor">Étage</label>
                <input z-input [(ngModel)]="formData.floor" name="floor" id="floor" placeholder="2" class="h-11" />
              </div>
              <div class="space-y-2">
                <label class="text-gray-700 text-sm" for="yearBuilt">Année de construction</label>
                <input z-input [(ngModel)]="formData.yearBuilt" name="yearBuilt" id="yearBuilt" placeholder="2010" class="h-11" />
              </div>
            </div>
            <div class="space-y-2">
              <label class="text-gray-700 text-sm" for="energyClass">Classe énergétique</label>
              <z-select [(ngModel)]="formData.energyClass" name="energyClass" id="energyClass" zPlaceholder="Sélectionnez une classe">
                <z-select-item zValue="A">A - Très performant</z-select-item>
                <z-select-item zValue="B">B - Performant</z-select-item>
                <z-select-item zValue="C">C - Assez performant</z-select-item>
                <z-select-item zValue="D">D - Moyen</z-select-item>
                <z-select-item zValue="E">E - Passable</z-select-item>
                <z-select-item zValue="F">F - Médiocre</z-select-item>
                <z-select-item zValue="G">G - Très médiocre</z-select-item>
              </z-select>
            </div>
          </z-card>

          <z-card class="p-6 border-0 shadow-sm space-y-4">
            <h2 class="text-lg text-gray-900">Loyer</h2>
            <div class="space-y-2">
              <label class="text-gray-700 text-sm" for="price">Montant mensuel (€)</label>
              <input z-input type="number" [(ngModel)]="formData.price" name="price" id="price" placeholder="850" class="h-11" required />
            </div>
          </z-card>

          <z-card class="p-6 border-0 shadow-sm space-y-4">
            <h2 class="text-lg text-gray-900">Description</h2>
            <div class="space-y-2">
              <label class="text-gray-700 text-sm" for="description">Description du bien (optionnel)</label>
              <textarea z-input [(ngModel)]="formData.description" name="description" id="description" placeholder="Décrivez votre bien..." class="min-h-[120px] resize-none"></textarea>
            </div>
          </z-card>

          <div class="flex gap-3 pt-4">
            <button z-button zType="outline" type="button" (click)="back.emit()" class="flex-1 h-12">
              Annuler
            </button>
            <button z-button type="submit" class="flex-1 bg-[#00A878] hover:bg-[#008f66] h-12">
              Enregistrer le bien
            </button>
          </div>
        </form>
      </div>
    </div>
  `,
})
export class AddPropertyFormComponent {
  readonly back = output<void>();
  readonly save = output<PropertyFormData>();

  protected formData: PropertyFormData = {
    name: '',
    type: '',
    address: '',
    city: '',
    postalCode: '',
    surface: '',
    rooms: '',
    price: '',
    description: '',
    yearBuilt: '',
    floor: '',
    energyClass: '',
  };

  protected handleSubmit(): void {
    this.save.emit({ ...this.formData });
  }
}
